// src/components/quiz/EncryptionDemo.tsx
import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin'
import { TextPlugin } from 'gsap/TextPlugin'

gsap.registerPlugin(ScrambleTextPlugin, TextPlugin)

// Full cipher character set — mixed case + digits + symbols
const CIPHER_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='

// Five Kenyan women names. Encrypted targets for name are name.length + 2 chars max.
// Email and phone encrypted targets match their original lengths.
const NAMES = [
  {
    name:     'Wanjiku',
    email:    'wanjiku@mail.co',
    phone:    '+254 712 345 678',
    encName:  'm54zx4hvN',       // 9 chars (7 + 2)
    encEmail: 'K9pXQr7nBs+W2Mj', // 15 chars
    encPhone: 'Zx8wRmN3Hq+6fDsJ', // 16 chars
  },
  {
    name:     'Atieno',
    email:    'atieno@mail.co',
    phone:    '+254 723 456 789',
    encName:  'K9pXQr7B',        // 8 chars (6 + 2)
    encEmail: 'Zx8wRmN3HqP6fD',  // 14 chars
    encPhone: 'Vn7tOkUy5eMlBp/J', // 16 chars
  },
  {
    name:     'Chebet',
    email:    'chebet@mail.co',
    phone:    '+254 734 567 890',
    encName:  'Zx8wRmN3',        // 8 chars (6 + 2)
    encEmail: 'c2VjdXJlZGRhdG',  // 14 chars
    encPhone: 'mNlUE1AAYlw+5rxQ', // 16 chars
  },
  {
    name:     'Nafula',
    email:    'nafula@mail.co',
    phone:    '+254 745 678 901',
    encName:  'c2Vj+XJl',        // 8 chars (6 + 2)
    encEmail: 'Vn7tOkUy5eMlBp',  // 14 chars
    encPhone: 'K9pXQr7nBsW2MjFk', // 16 chars
  },
  {
    name:     'Kerubo',
    email:    'kerubo@mail.co',
    phone:    '+254 756 789 012',
    encName:  'Vn7tOkUy',        // 8 chars (6 + 2)
    encEmail: 'm54zx4hvNlUE1A',  // 14 chars
    encPhone: 'c2VjdXJlZGRhdGFm', // 16 chars
  },
]

function sleep(ms: number) {
  return new Promise<void>(r => setTimeout(r, ms))
}

export function EncryptionDemo() {
  const nameEl  = useRef<HTMLDivElement>(null)
  const emailEl = useRef<HTMLDivElement>(null)
  const phoneEl = useRef<HTMLDivElement>(null)
  const lockEl  = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!nameEl.current || !emailEl.current || !phoneEl.current || !lockEl.current) return
    const nameDiv  = nameEl.current  as HTMLDivElement
    const emailDiv = emailEl.current as HTMLDivElement
    const phoneDiv = phoneEl.current as HTMLDivElement
    const lockDiv  = lockEl.current  as HTMLDivElement
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let alive = true
    let idx   = 0

    async function cycle() {
      while (alive) {
        const e = NAMES[idx % NAMES.length]

        // ── 1. Reset ──────────────────────────────────────────────────
        nameDiv.textContent  = ''
        emailDiv.textContent = ''
        phoneDiv.textContent = ''
        gsap.set([nameDiv, emailDiv, phoneDiv], { opacity: 1, color: 'var(--color-deep-teal)' })
        gsap.set(lockDiv, { opacity: 0 })

        // ── 2. Type all three in (staggered start, run concurrently) ──
        await Promise.all([
          new Promise<void>(r => gsap.to(nameDiv, {
            delay: 0,
            duration: e.name.length * 0.1,
            text: { value: e.name, delimiter: '' },
            ease: 'none', onComplete: r,
          })),
          new Promise<void>(r => gsap.to(emailDiv, {
            delay: 0.25,
            duration: e.email.length * 0.045,
            text: { value: e.email, delimiter: '' },
            ease: 'none', onComplete: r,
          })),
          new Promise<void>(r => gsap.to(phoneDiv, {
            delay: 0.5,
            duration: e.phone.length * 0.045,
            text: { value: e.phone, delimiter: '' },
            ease: 'none', onComplete: r,
          })),
        ])
        if (!alive) break

        // Hold
        await sleep(2000)
        if (!alive) break

        // ── 3. Scramble all three simultaneously ──────────────────────
        await Promise.all([
          new Promise<void>(r => gsap.to(nameDiv, {
            duration: 1.6,
            scrambleText: { text: e.encName, chars: CIPHER_CHARS, speed: 0.4, tweenLength: true },
            ease: 'none', onComplete: r,
          })),
          new Promise<void>(r => gsap.to(emailDiv, {
            duration: 1.6,
            scrambleText: { text: e.encEmail, chars: CIPHER_CHARS, speed: 0.4, tweenLength: false },
            ease: 'none', onComplete: r,
          })),
          new Promise<void>(r => gsap.to(phoneDiv, {
            duration: 1.6,
            scrambleText: { text: e.encPhone, chars: CIPHER_CHARS, speed: 0.4, tweenLength: false },
            ease: 'none', onComplete: r,
          })),
        ])
        if (!alive) break

        // ── 4. Encrypted badge fades in ───────────────────────────────
        await new Promise<void>(r =>
          gsap.to(lockDiv, { opacity: 1, duration: 0.4, ease: 'power2.out', onComplete: r })
        )
        if (!alive) break

        // Hold encrypted state
        await sleep(2200)
        if (!alive) break

        // ── 5. Fade out everything ────────────────────────────────────
        await new Promise<void>(r =>
          gsap.to([nameDiv, emailDiv, phoneDiv, lockDiv], {
            opacity: 0, duration: 0.4, ease: 'power2.in', onComplete: r,
          })
        )
        if (!alive) break

        await sleep(350)
        idx++
      }
    }

    cycle()
    return () => {
      alive = false
      gsap.killTweensOf([nameDiv, emailDiv, phoneDiv, lockDiv])
    }
  }, [])

  return (
    <div style={{
      background:          'rgba(255,255,255,0.62)',
      backdropFilter:      'blur(14px)',
      WebkitBackdropFilter:'blur(14px)',
      border:              '1px solid rgba(255,255,255,0.55)',
      borderRadius:        '16px',
      padding:             '1.25rem 1.375rem',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1.1rem' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="var(--color-teal)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700,
          letterSpacing: '0.11em', textTransform: 'uppercase', color: '#0a6b63',
        }}>
          Your identity is protected
        </span>
      </div>

      {/* Animated rows — fixed height container prevents layout jitter */}
      <div style={{ marginBottom: '1rem', height: '8rem', overflow: 'hidden' }}>
        {/* Name — fixed height so empty string never collapses the row */}
        <div ref={nameEl} style={{
          fontFamily: 'var(--font-sans)',
          fontSize:   '1.65rem',
          fontWeight: 700,
          color:      'var(--color-deep-teal)',
          letterSpacing: '0.02em',
          lineHeight: 1.2,
          height:     '2.3rem',
          overflow:   'hidden',
          marginBottom: '0.3rem',
        }} />

        {/* Email — fixed height */}
        <div ref={emailEl} style={{
          fontFamily: 'var(--font-sans)',
          fontSize:   '1rem',
          fontWeight: 600,
          color:      'var(--color-deep-teal)',
          letterSpacing: '0.01em',
          lineHeight: 1.4,
          height:     '1.5rem',
          overflow:   'hidden',
          marginBottom: '0.2rem',
        }} />

        {/* Phone — fixed height */}
        <div ref={phoneEl} style={{
          fontFamily: 'var(--font-sans)',
          fontSize:   '1rem',
          fontWeight: 600,
          color:      'var(--color-deep-teal)',
          letterSpacing: '0.01em',
          lineHeight: 1.4,
          height:     '1.5rem',
          overflow:   'hidden',
          marginBottom: '0.65rem',
        }} />

        {/* Encrypted badge — appears after scramble */}
        <div ref={lockEl} style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          background: 'rgba(13,148,136,0.1)',
          border: '1px solid rgba(13,148,136,0.25)',
          borderRadius: '99px',
          padding: '0.25rem 0.65rem',
          fontSize: '0.72rem', fontWeight: 600,
          color: '#0a6b63',
          opacity: 0,
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Encrypted
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'rgba(13,148,136,0.12)', marginBottom: '1rem' }} />

      {/* Static privacy bullets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
        {([
          ['Encrypted before storage', 'Your name, email, and phone number become unreadable before they reach our database'],
          ['Secure in transit',        'All data travels over HTTPS between your device and our servers'],
          ['Never shared',             'Not sold, not shared, not used for advertising of any kind'],
        ] as [string, string][]).map(([title, desc]) => (
          <div key={title} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--color-teal)', fontSize: '0.78rem', marginTop: '0.1rem', flexShrink: 0, fontWeight: 700 }}>✓</span>
            <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--color-slate)', fontWeight: 600 }}>{title}</strong>
              {' — '}
              <span style={{ color: '#334155' }}>{desc}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
